import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Cookie, X } from "lucide-react";
import { useAuth } from "../lib/AuthContext";
import { api } from "../lib/api";

// Slim bottom consent banner. Shows once until the visitor accepts/dismisses.
// Acceptance is persisted to the logged-in account (DB) as well as localStorage,
// so it SURVIVES a fork / new preview origin (which wipes localStorage).
export default function CookieNotice() {
    const { user } = useAuth();
    const impersonating = typeof window !== "undefined" && !!localStorage.getItem("rosterly_owner_token");
    const [dismissed, setDismissed] = useState(() => typeof window !== "undefined" && localStorage.getItem("rosterly_cookie_ack") === "yes");
    // A logged-in account that has accepted the Terms never sees the banner again,
    // on any device or preview URL (read from the DB user record).
    const accountAccepted = !!(user && user.tos_accepted_at);
    // While the owner impersonates another account, never show the cookie consent —
    // accepting it here must not count as the impersonated user's consent.
    if (impersonating || dismissed || accountAccepted) return null;
    const accept = () => {
        localStorage.setItem("rosterly_cookie_ack", "yes");
        localStorage.setItem("rosterly_tos_agreed", "yes");
        setDismissed(true);
        if (user && localStorage.getItem("rosterly_token")) api.post("/account/accept-terms").catch(() => {});
    };
    return (
        <div
            className="fixed inset-x-0 bottom-0 z-[80] border-t border-[#1E2233] bg-[#08090D]/95 backdrop-blur-xl"
            data-testid="cookie-notice"
        >
            <div className="w-full px-4 md:px-8 lg:px-12 py-3 max-w-[1600px] mx-auto flex flex-col sm:flex-row items-start sm:items-center gap-3">
                <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#9D4CDD]/15 text-[#9D4CDD]">
                    <Cookie className="h-4 w-4" />
                </span>
                <p className="text-[12px] text-[#94A3B8] leading-relaxed flex-1">
                    Rosterly uses cookies to keep you signed in, remember preferences, and measure performance. By continuing you agree to our{" "}
                    <Link to="/terms" className="text-[#9D4CDD] hover:text-[#B366F0] font-bold" data-testid="cookie-terms-link">Terms of Service</Link>
                    {" "}and{" "}
                    <Link to="/privacy" className="text-[#9D4CDD] hover:text-[#B366F0] font-bold" data-testid="cookie-privacy-link">Privacy Policy</Link>.
                </p>
                <div className="flex items-center gap-2 shrink-0">
                    <button onClick={accept} data-testid="cookie-accept-btn"
                        className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-[#9D4CDD] hover:bg-[#B366F0] text-white font-black uppercase tracking-wider text-[11px] transition-colors">
                        Accept
                    </button>
                    <button onClick={accept} data-testid="cookie-dismiss-btn" aria-label="Dismiss"
                        className="inline-flex items-center justify-center h-8 w-8 rounded-full border border-[#1E2233] text-[#94A3B8] hover:text-white hover:border-[#9D4CDD]/60 transition-colors">
                        <X className="h-4 w-4" />
                    </button>
                </div>
            </div>
        </div>
    );
}
